import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import type {ProyectoAccionSeguimientoDTO, ProyectoFormDTO} from '../../../../api/Objetos';
import {accionesSeguimientoApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {DateField, FieldGrid, SelectField, TextAreaField, TextField} from '../../../../ui/form/fields';
import {buildCardTitle, buildSummary, EmptyState, FormSection, hasPersistedId, ItemCard, useProjectReadOnly} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';

const actionTypes = [
    'Correo',
    'Requerimiento',
    'Informe',
    'Reunión',
    'Aviso',
    'Recepción documental',
    'Subsanación',
    'Recordatorio a empresa',
    'Comunicación con Ministerio',
    'Seguimiento de obra',
    'Seguimiento de certificaciones',
    'Seguimiento de informes',
    'Otro',
];

const actionStatuses = ['Pendiente', 'Realizada', 'Resuelta'];

export function AccionesSeguimientoSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'acciones-seguimiento'],
        proyectoId: props.proyectoId,
        getFn: accionesSeguimientoApi.list,
        createFn: accionesSeguimientoApi.create,
        updateFn: accionesSeguimientoApi.update,
        deleteFn: accionesSeguimientoApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), accionesSeguimiento: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async value => {
        await section.save(value.accionesSeguimiento ?? []);
    });

    useRegisterSectionSave({
        id: 'acciones-seguimiento',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => handleSave(),
    });

    return (
        <FormProvider {...form}>
            <AccionesSeguimientoFields showIds={Boolean(props.showIds)} loadError={section.error}/>
            {!readOnly && <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>}
        </FormProvider>
    );
}

function AccionesSeguimientoFields(props: { showIds: boolean; loadError: unknown }) {
    const readOnly = useProjectReadOnly();
    const {t} = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const acciones = useFieldArray({control: form.control, name: 'accionesSeguimiento'});

    return (
        <FormSection
            id="acciones-seguimiento"
            title={t('accionesSeguimientoSection.title', {count: acciones.fields.length})}
            collapsible
            defaultOpen={false}
        >
            <SectionAlerts loadError={props.loadError}/>
            {acciones.fields.length === 0 && <EmptyState message={t('accionesSeguimientoSection.empty')}/>}
            {acciones.fields.map((field, index) => (
                <AccionCard key={field.id} index={index} showIds={props.showIds} onRemove={() => acciones.remove(index)}/>
            ))}
            {!readOnly && <button
                className="btn btnSecondary"
                type="button"
                onClick={() => acciones.prepend({
                    fecha: new Date().toISOString().slice(0, 10),
                    tipoAccion: 'Correo',
                    descripcion: '',
                    responsable: '',
                    estado: 'Pendiente',
                    documentoEnlace: '',
                })}
            >
                {t('accionesSeguimientoSection.add')}
            </button>}
        </FormSection>
    );
}

function AccionCard(props: { index: number; showIds: boolean; onRemove: () => void }) {
    const {t} = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const action = form.watch(`accionesSeguimiento.${props.index}` as const) as ProyectoAccionSeguimientoDTO | undefined;
    if (!action) return null;

    return (
        <ItemCard
            title={buildCardTitle(t, t('accionesSeguimientoSection.action'), props.index, action.descripcion, action.tipoAccion, action.fecha)}
            summary={buildSummary(t, action.fecha, action.tipoAccion, action.estado, action.responsable)}
            onRemove={props.onRemove}
            defaultOpen={!hasPersistedId(action.id)}
        >
            <FieldGrid>
                {props.showIds && hasPersistedId(action.id) ? (
                    <div className="label">
                        ID
                        <input className="input" value={action.id} readOnly/>
                    </div>
                ) : null}
                <DateField label={t('accionesSeguimientoSection.date')} name={`accionesSeguimiento.${props.index}.fecha`} register={form.register}/>
                <SelectField
                    label={t('accionesSeguimientoSection.type')}
                    name={`accionesSeguimiento.${props.index}.tipoAccion`}
                    register={form.register}
                    options={actionTypes.map(value => ({value, label: value}))}
                />
                <SelectField
                    label={t('accionesSeguimientoSection.status')}
                    name={`accionesSeguimiento.${props.index}.estado`}
                    register={form.register}
                    options={actionStatuses.map(value => ({value, label: value}))}
                />
                <TextField label={t('accionesSeguimientoSection.responsible')} name={`accionesSeguimiento.${props.index}.responsable`} register={form.register}/>
                <TextField label={t('accionesSeguimientoSection.document')} name={`accionesSeguimiento.${props.index}.documentoEnlace`} register={form.register}/>
            </FieldGrid>
            <TextAreaField
                label={t('accionesSeguimientoSection.description')}
                name={`accionesSeguimiento.${props.index}.descripcion`}
                register={form.register}
                rows={4}
            />
        </ItemCard>
    );
}
